import React, { useState, useEffect } from 'react';
import { X, Shield, Copy, Check, Download } from 'lucide-react';
import QRCode from 'qrcode';
import { neonQuery } from '../services/api';

// PR 1c — Self-serve MFA enrollment / management modal. Triggered from TopBar's Security button.
// Three states:
//   - status: `loading` | `enabled` | `disabled`
//   - if disabled: shows enroll button → enroll begin → QR + manual code + first-code input → confirm → display backup codes
//   - if enabled: shows status (enabled date, last verified, backup codes remaining) + admin contact note for reset
export default function MfaEnrollModal({ onClose }) {
  const [status, setStatus] = useState('loading');
  const [statusDetail, setStatusDetail] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState(null);  // { secretBase32, otpauthUri, issuer, label }
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackups, setCopiedBackups] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await neonQuery('mfa_status');
        if (cancelled) return;
        if (r.enabled) { setStatus('enabled'); setStatusDetail(r); }
        else { setStatus('disabled'); }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When enrollData arrives, render the otpauth URI as a QR code data URL.
  useEffect(() => {
    if (!enrollData?.otpauthUri) return;
    QRCode.toDataURL(enrollData.otpauthUri, { width: 220, margin: 1 })
      .then(setQrDataUrl)
      .catch(e => setError('QR render failed: ' + e.message));
  }, [enrollData?.otpauthUri]);

  const beginEnroll = async () => {
    setError(null);
    setEnrolling(true);
    try {
      const data = await neonQuery('mfa_enroll_begin');
      setEnrollData(data);
    } catch (e) { setError(e.message); }
    finally { setEnrolling(false); }
  };
  const confirmEnroll = async () => {
    if (!/^\d{6}$/.test(confirmCode)) { setError('Enter the 6-digit code from your authenticator.'); return; }
    setError(null);
    setEnrolling(true);
    try {
      const data = await neonQuery('mfa_enroll_confirm', { code: confirmCode });
      if (!data?.enabled) { setError(data?.error || 'Confirmation failed.'); return; }
      setBackupCodes(data.backupCodes);
      setStatus('enabled');
    } catch (e) { setError(e.message); }
    finally { setEnrolling(false); }
  };
  const copyText = (text, which) => {
    navigator.clipboard.writeText(text);
    if (which === 'secret') { setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 2000); }
    if (which === 'backups') { setCopiedBackups(true); setTimeout(() => setCopiedBackups(false), 2000); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px 20px', zIndex: 1100, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', border: '2px solid var(--navy)', borderRadius: 8, padding: 24, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Shield size={20} color="var(--navy)" />
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginLeft: 8 }}>Two-Factor Authentication</div>
          <button onClick={onClose} title="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4 }}><X size={18} /></button>
        </div>

        {error && <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}

        {status === 'loading' && <div style={{ padding: 20, color: 'var(--gray-500)' }}>Checking status...</div>}

        {status === 'enabled' && !backupCodes && (
          <div>
            <div style={{ background: '#ecfdf5', border: '1px solid var(--green)', borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 13, color: 'var(--green)' }}>
              <strong>Enabled.</strong> You will be prompted for a 6-digit code each time you sign in with your password.
            </div>
            <table style={{ width: '100%', fontSize: 12, color: 'var(--gray-700)' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0' }}>Enabled since:</td><td style={{ textAlign: 'right' }}>{new Date(statusDetail.enabledAt).toLocaleDateString()}</td></tr>
                {statusDetail.lastVerifiedAt && <tr><td style={{ padding: '4px 0' }}>Last verified:</td><td style={{ textAlign: 'right' }}>{new Date(statusDetail.lastVerifiedAt).toLocaleString()}</td></tr>}
                <tr><td style={{ padding: '4px 0' }}>Backup codes remaining:</td><td style={{ textAlign: 'right' }}>{statusDetail.backupCodesRemaining} of 8</td></tr>
              </tbody>
            </table>
            <div style={{ marginTop: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 6, fontSize: 11, color: 'var(--gray-600)', lineHeight: 1.5 }}>
              To disable or reset MFA, contact an admin. They can clear your enrollment from User Management → Reset MFA.
            </div>
          </div>
        )}

        {status === 'disabled' && !enrollData && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.5 }}>
              Two-factor authentication adds a code from your phone to every password login. Without it, someone with your password can sign in. With it, they need your password <em>and</em> your phone.
            </p>
            <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.5 }}>
              You will need an authenticator app: <strong>Authy</strong>, <strong>Google Authenticator</strong>, <strong>1Password</strong>, or similar.
            </p>
            <button onClick={beginEnroll} disabled={enrolling} style={{ width: '100%', padding: 12, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: enrolling ? 'default' : 'pointer', marginTop: 8 }}>
              {enrolling ? 'Starting...' : 'Set up two-factor authentication'}
            </button>
          </div>
        )}

        {status === 'disabled' && enrollData && !backupCodes && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--gray-700)', marginTop: 0 }}>Scan this QR code with your authenticator app, then enter the 6-digit code it displays.</p>
            {qrDataUrl ? (
              <div style={{ textAlign: 'center', margin: '12px 0' }}>
                <img src={qrDataUrl} alt="MFA QR code" style={{ border: '1px solid var(--gray-200)', borderRadius: 6 }} />
              </div>
            ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Rendering QR...</div>}
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Can't scan? Type this code into your app:</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 16 }}>
              <code style={{ flex: 1, background: 'var(--gray-50)', padding: '8px 10px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{enrollData.secretBase32}</code>
              <button onClick={() => copyText(enrollData.secretBase32, 'secret')} style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', cursor: 'pointer', fontSize: 11 }}>
                {copiedSecret ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Enter the 6-digit code from your app:</label>
            <input
              type="text"
              value={confirmCode}
              onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              style={{ width: '100%', padding: '10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 22, textAlign: 'center', letterSpacing: 8, fontWeight: 700, fontFamily: 'monospace', boxSizing: 'border-box' }}
            />
            <button onClick={confirmEnroll} disabled={enrolling || confirmCode.length < 6} style={{ width: '100%', padding: 12, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 12 }}>
              {enrolling ? 'Verifying...' : 'Confirm'}
            </button>
          </div>
        )}

        {backupCodes && (
          <div>
            <div style={{ background: '#ecfdf5', border: '1px solid var(--green)', borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 13, color: 'var(--green)', fontWeight: 700 }}>
              Two-factor authentication is now enabled.
            </div>
            <p style={{ fontSize: 13, color: 'var(--gray-700)' }}>
              <strong>Save these 8 backup codes somewhere safe.</strong> Each works once if you lose access to your authenticator. They will <em>not</em> be shown again.
            </p>
            <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 6, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8, marginBottom: 12 }}>
              {backupCodes.map(c => <div key={c}>{c}</div>)}
            </div>
            <button onClick={() => copyText(backupCodes.join('\n'), 'backups')} style={{ width: '100%', padding: 10, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {copiedBackups ? <><Check size={12} /> Copied — paste them into a safe place</> : <><Copy size={12} /> Copy all backup codes</>}
            </button>
            <button onClick={onClose} style={{ width: '100%', padding: 10, background: 'var(--gray-200)', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
              I've saved them — close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
